// 835 — the electronic remittance advice.
//
// Moved out of pages/finance/BillingRun.js, where it sat as ~100 lines of
// untested parsing inside a 2,200-line component. The logic is unchanged
// except that delimiters now come from the shared x12 reader rather than a
// second hand-rolled ISA slice, and the claim status codes are named.
//
// AHFC has never received an 835 — remittances arrive as GAMMIS portal PDFs
// and are read by `parse-ra`. This is the path that replaces that once ERA
// delivery is enrolled, and the two converge on the same posting decision in
// remitPosting.js.

import { parse } from './x12.js';

/** CLP02 — the payer's verdict on a claim. */
export const CLAIM_STATUS = {
  1:  'paid',      // processed as primary
  2:  'paid',      // processed as secondary
  3:  'paid',      // processed as tertiary
  4:  'denied',
  19: 'paid',      // processed as primary, forwarded
  20: 'paid',      // processed as secondary, forwarded
  21: 'paid',      // processed as tertiary, forwarded
  22: 'voided',    // reversal of previous payment
  23: 'paid',      // not our claim, forwarded
};

const money = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/** CCYYMMDD → YYYY-MM-DD. Anything else is passed through untouched. */
const isoDate = (raw = '') =>
  /^\d{8}$/.test(raw) ? `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}` : raw;

/**
 * Parse an 835 into claims, service lines and adjustments.
 *
 * Returns null when the text is not an interchange at all, so a user who picks
 * the wrong file gets told rather than shown an empty result.
 */
export const parse835 = (text) => {
  const { segments, delimiters } = parse(text);
  if (!segments.length) return null;

  const claims = [];
  let claim = null;
  let line = null;
  let checkDate = '';
  let checkNumber = '';
  let totalPayment = 0;
  let payer = '';

  for (const seg of segments) {
    const id = seg[0];

    if (id === 'BPR') totalPayment = money(seg[2]);
    if (id === 'TRN') checkNumber = seg[2] || '';
    if (id === 'DTM' && seg[1] === '405') checkDate = isoDate(seg[2]);
    if (id === 'N1' && seg[1] === 'PR') payer = seg[2] || '';

    if (id === 'CLP') {
      if (claim) claims.push(claim);
      line = null;
      const statusCode = seg[2] || '';
      claim = {
        // CLP01 is whatever we put in CLM01 on the 837P — the claim_lines id.
        // That is what turns matching from a heuristic into a lookup.
        claimControlNumber: seg[1] || '',
        statusCode,
        status: CLAIM_STATUS[Number(statusCode)] || 'paid',
        billed: money(seg[3]),
        paid: money(seg[4]),
        patientResponsibility: money(seg[5]),
        icn: seg[7] || '',
        medicaidId: '',
        patientName: '',
        serviceLines: [],
        adjustments: [],
        checkDate,
        checkNumber,
      };
      continue;
    }

    if (id === 'NM1' && seg[1] === 'QC' && claim) {
      claim.patientName = `${seg[3] || ''}, ${seg[4] || ''}`.trim().replace(/^,\s*/, '').replace(/,\s*$/, '');
      claim.medicaidId = seg[9] || '';
    }

    if (id === 'SVC' && claim) {
      const proc = (seg[1] || '').split(delimiters.component);
      line = {
        serviceCode: proc[1] || proc[0] || '',
        modifier: proc[2] || '',
        billed: money(seg[2]),
        paid: money(seg[3]),
        units: money(seg[5]),
        fromDate: '',
        toDate: '',
        adjustments: [],
      };
      claim.serviceLines.push(line);
      continue;
    }

    if (id === 'DTM' && line) {
      if (seg[1] === '232') line.fromDate = isoDate(seg[2]);
      if (seg[1] === '233') line.toDate = isoDate(seg[2]);
    }

    if (id === 'CAS') {
      // CAS repeats group/code/amount in threes: CAS*CO*45*572.16*~ and a
      // segment can carry up to six of them. Reading only the first, as the
      // previous parser did, loses every adjustment after the leading one.
      const group = seg[1] || '';
      for (let i = 2; i + 1 < seg.length; i += 3) {
        const code = seg[i];
        const amount = money(seg[i + 1]);
        if (!code) continue;
        const adj = { group, code, amount };
        if (line) line.adjustments.push(adj);
        else if (claim) claim.adjustments.push(adj);
      }
    }
  }
  if (claim) claims.push(claim);

  return { claims, checkDate, checkNumber, totalPayment, payer };
};
